import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CircleAlert, Inbox, LoaderCircle } from 'lucide-react';

/** Estados obrigatórios de toda tela: vazio, carregando e erro (regra geral da spec). */
export function Vazio({ titulo, texto, acao }: { titulo: string; texto?: string; acao?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-6 py-16 text-center">
      <Inbox size={28} strokeWidth={1.5} className="text-tinta-3" />
      <p className="font-medium">{titulo}</p>
      {texto && <p className="max-w-md text-sm text-tinta-3">{texto}</p>}
      {acao}
    </div>
  );
}

export function Carregando() {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-16 text-sm text-tinta-3">
      <LoaderCircle size={18} strokeWidth={1.75} className="animate-spin" />
      {t('estado.carregando')}
    </div>
  );
}

/**
 * ROB-R9-01 (rodada 9): com `erro` e `voltar`, a ficha de registro que não existe (404, ou 400 de id inválido) mostra
 * "Registro não encontrado" com o caminho de volta para a lista; o erro genérico com "Tentar de novo" fica para falha
 * do servidor e de rede.
 */
export function Erro({ aoTentar, erro, voltar }: { aoTentar?: () => void; erro?: unknown; voltar?: string }) {
  const { t } = useTranslation();
  const status = (erro as { status?: number } | null)?.status;
  if (voltar && (status === 404 || status === 400)) {
    return (
      <div role="status" className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-6 py-12 text-center">
        <Inbox size={26} strokeWidth={1.5} className="text-tinta-3" />
        <p className="font-medium">{t('estado.naoEncontrado')}</p>
        <p className="text-sm text-tinta-3">{t('estado.naoEncontradoTexto')}</p>
        <Link to={voltar} className="h-9 content-center rounded-lg border border-borda px-4 text-sm hover:bg-sup-2">
          {t('estado.voltarLista')}
        </Link>
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-col items-center gap-3 rounded-xl border border-perigo/40 bg-perigo/5 px-6 py-12 text-center">
      <CircleAlert size={26} strokeWidth={1.5} className="text-perigo" />
      <p className="font-medium">{t('estado.erroTitulo')}</p>
      <p className="text-sm text-tinta-3">{t('estado.erroTexto')}</p>
      {aoTentar && (
        <button type="button" onClick={aoTentar} className="h-9 rounded-lg border border-borda px-4 text-sm hover:bg-sup-2">
          {t('estado.tentar')}
        </button>
      )}
    </div>
  );
}

/**
 * ROB-01 (rodada 6) · Cartão de indicador do Início. Carregando mostra reticências; erro mostra um traço e "Tentar de
 * novo" no próprio cartão (antes o cartão ficava em "..." para sempre quando a API falhava). Opcionais: `destaque`
 * (borda e valor em laranja, para atenção) e `para` (o cartão vira link quando há dado; em erro o botão fica fora do
 * link). Valor longo quebra linha em vez de estourar o cartão no mobile.
 */
export function CartaoIndicador({
  rotulo,
  valor,
  consultas,
  detalhe,
  destaque,
  para,
}: {
  rotulo: string;
  valor: ReactNode | null | undefined;
  consultas: { isError: boolean; isPending?: boolean; refetch: () => unknown }[];
  detalhe?: ReactNode;
  destaque?: boolean;
  para?: string;
}) {
  const { t } = useTranslation();
  const erro = consultas.some((c) => c.isError);
  // SB-R11-01 (rodada 11): o número nunca quebra; a fonte acompanha a largura do cartão (container query).
  const caixa = `@container block min-w-0 rounded-xl border bg-sup-1 p-4 ${destaque && !erro ? 'border-laranja/50' : 'border-borda'}`;
  const conteudo = (
    <>
      <p className="kicker mb-2">{rotulo}</p>
      {erro ? (
        <div className="flex items-baseline gap-3">
          <p className="num text-3xl font-medium text-tinta-3" aria-label={t('estado.erroTexto')}>
            -
          </p>
          <button type="button" onClick={() => consultas.filter((c) => c.isError).forEach((c) => void c.refetch())} className="text-sm text-acento-texto hover:underline">
            {t('estado.tentar')}
          </button>
        </div>
      ) : (
        <p className={`num whitespace-nowrap text-xl font-medium @3xs:text-2xl @2xs:text-3xl ${destaque ? 'text-laranja' : ''}`}>{valor ?? '...'}</p>
      )}
      {!erro && detalhe}
    </>
  );
  return para && !erro && valor != null ? (
    <Link to={para} className={`${caixa} hover:border-tinta-3`}>
      {conteudo}
    </Link>
  ) : (
    <div className={caixa}>{conteudo}</div>
  );
}
